import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { PermissionService } from './permission.service';
import { ModuleFlagsService } from './module-flags.service';
import { NAV_GROUPS } from './nav-groups';

export interface NavItem {
  label: string;
  route: string;
  /** Legacy role gate — used only when RAC permission data is unavailable. */
  roles?: string[];
  /** RAC page key (backend page-registry) that controls this item. */
  permissionKey?: string;
  queryParams?: any;
  subRoutes?: string[]; // Routes that should trigger active state for this item
}

export interface NavCategory {
  id: string;
  label: string;
  icon: string;
  roles?: string[];
  /** Only the SUPER_ADMIN role sees it (tenant Admins do not). */
  superAdminOnly?: boolean;
  clientType?: string;
  /**
   * Configuration switch (ModuleFlagsService) that must be ON for the whole
   * category to show, e.g. 'b2b.enabled'. Hidden until the flags are loaded.
   */
  settingKey?: string;
  items: NavItem[];
}

/** A rail entry: one or more categories (modules) shown together. */
export interface NavGroup {
  id: string;
  label: string;
  icon: string;
  categories: NavCategory[];
}

export interface NavCrumb {
  label: string;
  route?: string;
  queryParams?: any;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private moduleFlags = inject(ModuleFlagsService);
  private router = inject(Router);

  public readonly menuStructure: NavCategory[] = [
    {
      // Visible to everyone: the overview only shows modules the user can read.
      id: 'dashboard',
      label: 'Overview',
      icon: 'bi-speedometer2',
      items: [{ label: 'Operations overview', route: '/dashboard' }],
    },
    {
      id: 'products',
      label: 'Products',
      icon: 'bi-box-seam',
      roles: [
        'Admin',
        'WAREHOUSE_MANAGER',
        'CATEGORY_MANAGER',
        'MIS_TEAM',
        'GRN_TEAM',
        'LP_TEAM',
        'AREA_MANAGER',
        'STORE_MANAGER',
        'ASM',
      ],
      items: [
        {
          label: 'Products List',
          route: '/product-list',
          permissionKey: 'products.list',
          subRoutes: ['/edit-product', '/add-product', '/bulk-add-products'],
        },
        {
          label: 'HSN Exceptions',
          route: '/hsn-exceptions',
          permissionKey: 'products.list',
        },
        {
          label: 'Vendor Mapping',
          route: '/product-vendor-mapping',
          permissionKey: 'products.vendor-mapping',
          roles: [
            'Admin',
            'STORE_MANAGER',
            'WAREHOUSE_MANAGER',
            'MIS_TEAM',
            'GRN_TEAM',
          ],
        },
      ],
    },
    {
      id: 'vendors',
      label: 'Vendors',
      icon: 'bi-building',
      roles: ['Admin', 'CATEGORY_MANAGER', 'MIS_TEAM'],
      items: [
        {
          label: 'Vendors List',
          route: '/vendors',
          permissionKey: 'vendors.list',
          subRoutes: ['/edit-vendor', '/add-vendor'],
        },
        {
          label: 'Vendor SKU Mapping',
          route: '/vendor-sku-upload',
          permissionKey: 'vendors.sku-upload',
        },
        {
          label: 'Manufacturers List',
          route: '/manufacturers',
          permissionKey: 'vendors.manufacturers',
          subRoutes: ['/edit-manufacturer', '/add-manufacturers'],
        },
      ],
    },
    {
      // B2B Sales: wholesale customers and orders. Only shown when the
      // client has switched the module on under Configuration > B2B Sales.
      id: 'b2b',
      label: 'B2B Sales',
      icon: 'bi-people',
      roles: ['Admin', 'SUPER_ADMIN', 'STORE_MANAGER', 'MIS_TEAM'],
      settingKey: 'b2b.enabled',
      items: [
        {
          label: 'Orders Management',
          route: '/b2b-orders/dashboard',
          subRoutes: ['/b2b-orders'],
          permissionKey: 'b2b.orders',
          roles: ['Admin', 'SUPER_ADMIN', 'STORE_MANAGER', 'MIS_TEAM'],
        },
        {
          label: 'B2B Customers',
          route: '/b2b-customer',
          subRoutes: ['/add-b2b-customer', '/edit-b2b-customer'],
          permissionKey: 'b2b.customers',
          roles: ['Admin', 'SUPER_ADMIN', 'STORE_MANAGER', 'MIS_TEAM'],
        },
      ],
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: 'bi-receipt',
      roles: [
        'Cashier',
        'CASHIER',
        'Admin',
        'STORE_MANAGER',
        'AREA_MANAGER',
        'ASM',
      ],
      items: [
        { label: 'Billing', route: '/billing', permissionKey: 'billing.pos' },
        {
          label: 'Cart Audit',
          route: '/billing/cart-audit',
          permissionKey: 'billing.cart-audit',
          roles: ['Admin', 'STORE_MANAGER', 'AREA_MANAGER', 'ASM', 'MANAGER'],
        },
      ],
    },
    {
      id: 'purchase',
      label: 'Purchase',
      icon: 'bi-cart-plus',
      roles: [
        'Admin',
        'CATEGORY_MANAGER',
        'STORE_MANAGER',
        'MIS_TEAM',
        'ASM',
        'AREA_MANAGER',
        'PRN_TEAM',
      ],
      items: [
        {
          label: 'Purchase Orders',
          route: '/po',
          permissionKey: 'purchase.po',
          roles: [
            'Admin',
            'CATEGORY_MANAGER',
            'STORE_MANAGER',
            'MIS_TEAM',
            'ASM',
            'AREA_MANAGER',
          ],
          subRoutes: ['/po/create-po', '/po/edit-po'],
        },
        {
          label: 'Purchase Return Notes',
          route: '/prn',
          permissionKey: 'purchase.prn',
          roles: [
            'Admin',
            'STORE_MANAGER',
            'CATEGORY_MANAGER',
            'MIS_TEAM',
            'PRN_TEAM',
            'ASM',
            'AREA_MANAGER',
          ],
          subRoutes: ['/prn/create', '/prn/edit'],
        },
      ],
    },
    {
      id: 'grn',
      label: 'GRN',
      icon: 'bi-box-arrow-in-right',
      roles: [
        'Admin',
        'CATEGORY_MANAGER',
        'STORE_MANAGER',
        'MIS_TEAM',
        'GRN_TEAM',
        'AREA_MANAGER',
        'ASM',
      ],
      items: [
        {
          label: 'Goods Receipt Notes List',
          route: '/grn',
          permissionKey: 'grn.list',
          subRoutes: ['/grn/create-grn', '/grn/edit'],
        },
      ],
    },
    {
      id: 'transfers',
      label: 'Transfers',
      icon: 'bi-shuffle',
      roles: [
        'Admin',
        'STORE_MANAGER',
        'WAREHOUSE_MANAGER',
        'MIS_TEAM',
        'INWARD_TEAM',
        'ASM',
        'AREA_MANAGER',
      ],
      items: [
        {
          label: 'Transfer Out',
          route: '/transfer-out',
          permissionKey: 'transfers.out',
          subRoutes: [
            '/transfer-out/create',
            '/transfer-out/edit',
            '/transfer-out/eway-bill/create',
          ],
        },
        {
          label: 'Transfer In',
          route: '/transfer-in',
          permissionKey: 'transfers.in',
          subRoutes: ['/transfer-in/create', '/transfer-in/edit'],
        },
        {
          label: 'E-Way Bill',
          route: '/transfer-out',
          permissionKey: 'transfers.eway-bill',
          queryParams: { tab: 'eway_bill' },
          subRoutes: ['/transfer-out/eway-bill/create'],
        },
        {
          label: 'Print Box QR',
          route: '/transfer-box/print',
          permissionKey: 'transfers.box-print',
        },
        {
          label: 'Dispatch Trips',
          route: '/transfer-box/trips',
          permissionKey: 'transfers.trips',
          subRoutes: ['/transfer-box/dispatch-sheet'],
        },
      ],
    },
    {
      id: 'stock',
      label: 'Stock Management',
      icon: 'bi-clipboard-pulse',
      roles: [
        'Admin',
        'STORE_MANAGER',
        'WAREHOUSE_MANAGER',
        'CATEGORY_MANAGER',
        'MIS_TEAM',
        'ASM',
        'AREA_MANAGER',
        'INWARD_TEAM',
        'LP_TEAM',
        'PRN_TEAM',
      ],
      items: [
        {
          label: 'Stock Status',
          route: '/inventory/list',
          permissionKey: 'stock.status',
          subRoutes: ['/inventory/add', '/inventory/transfer'],
        },
        {
          label: 'Stock Take',
          route: '/stocktake',
          permissionKey: 'stock.take',
          roles: ['Admin', 'MIS_TEAM', 'LP_TEAM'],
          subRoutes: [
            '/stocktake/create',
            '/stocktake/edit',
            '/stocktake/session',
          ],
        },
        {
          label: 'Stock Movement',
          route: '/stock-movement',
          permissionKey: 'stock.movement',
          roles: ['Admin', 'MIS_TEAM', 'STORE_MANAGER'],
        },
        {
          label: 'Bulk Breakup',
          route: '/repack',
          permissionKey: 'stock.repack',
          roles: ['Admin', 'WAREHOUSE_MANAGER', 'CATEGORY_MANAGER', 'MIS_TEAM'],
          subRoutes: ['/repack/create-repack', '/repack/edit'],
        },
        {
          label: 'Stock Write Off',
          route: '/stock-write-off',
          permissionKey: 'stock.write-off',
          roles: ['Admin', 'STORE_MANAGER'],
          subRoutes: ['/stock-write-off/create'],
        },
        {
          label: 'Kit Master',
          route: '/kit-master',
          permissionKey: 'stock.kit-master',
          roles: ['Admin', 'WAREHOUSE_MANAGER', 'CATEGORY_MANAGER', 'MIS_TEAM'],
        },
        {
          label: 'Kit Preparation',
          route: '/kit-preparation',
          permissionKey: 'stock.kit-preparation',
          roles: ['Admin', 'WAREHOUSE_MANAGER', 'CATEGORY_MANAGER', 'MIS_TEAM'],
        },
      ],
    },
    {
      id: 'mbq',
      label: 'MBQ',
      icon: 'bi-journal-check',
      roles: ['Admin', 'CATEGORY_MANAGER', 'MIS_TEAM'],
      items: [
        { label: 'MBQ Drafts', route: '/mbq/list', permissionKey: 'mbq.drafts' },
        { label: 'Upload MBQ', route: '/mbq/upload', permissionKey: 'mbq.upload' },
        { label: 'Create MBQ', route: '/mbq/create-mbq', permissionKey: 'mbq.create' },
      ],
    },
    {
      id: 'indent',
      label: 'Indent',
      icon: 'bi-list-check',
      roles: ['Admin', 'WAREHOUSE_MANAGER', 'CATEGORY_MANAGER', 'MIS_TEAM'],
      items: [
        {
          label: 'Indent List',
          route: '/indent-list',
          permissionKey: 'indent.list',
          subRoutes: ['/indent-list/create-indent'],
        },
      ],
    },
    {
      id: 'picklist',
      label: 'Picklist',
      icon: 'bi-view-list',
      roles: ['Admin', 'WAREHOUSE_MANAGER'],
      items: [
        {
          label: 'Picklist Master',
          route: '/picklist',
          permissionKey: 'picklist.master',
          subRoutes: ['/picklist/create', '/picklist/edit'],
        },
      ],
    },
    {
      id: 'services',
      label: 'Services',
      icon: 'bi-wrench',
      roles: ['Admin', 'STORE_MANAGER'],
      clientType: 'Clinic',
      items: [
        {
          label: 'Service List',
          route: '/services',
          permissionKey: 'services.list',
          subRoutes: ['/services/create', '/services/edit'],
        },
      ],
    },
    {
      id: 'schemes',
      label: 'Schemes',
      icon: 'bi-percent',
      roles: [
        'Admin',
        'STORE_MANAGER',
        'WAREHOUSE_MANAGER',
        'MIS_TEAM',
        'GRN_TEAM',
        'CATEGORY_MANAGER',
      ],
      items: [
        {
          label: 'Offers List',
          route: '/offers-list',
          permissionKey: 'schemes.offers',
          subRoutes: ['/create-offer'],
        },
        {
          // Lane D returns inbox + history.
          label: 'Returns',
          route: '/returns',
          permissionKey: 'schemes.returns',
        },
        {
          // Lane B return-policy admin editor.
          label: 'Return Policy',
          route: '/return-policy',
          permissionKey: 'schemes.return-policy',
        },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: 'bi-graph-up-arrow',
      roles: [
        'Admin',
        'STORE_MANAGER',
        'MIS_TEAM',
        'CATEGORY_MANAGER',
        'WAREHOUSE_MANAGER',
      ],
      items: [
        {
          label: 'Reports Dashboard',
          route: '/reports',
          permissionKey: 'reports.dashboard',
          roles: ['Admin', 'STORE_MANAGER'],
        },
        {
          label: 'Detailed Report',
          route: '/detailed-report',
          permissionKey: 'reports.detailed',
          roles: ['Admin', 'STORE_MANAGER'],
        },
        {
          label: 'Bill Summary List',
          route: '/billinglist',
          permissionKey: 'reports.bill-summary',
          roles: ['Admin', 'STORE_MANAGER'],
        },
        {
          label: 'Toola Sync',
          route: '/toola-sync',
          permissionKey: 'reports.toola-sync',
          roles: ['Admin', 'STORE_MANAGER'],
        },
      ],
    },
    {
      id: 'terminals',
      label: 'Terminals',
      icon: 'bi-pc-display',
      roles: ['Admin', 'STORE_MANAGER', 'AREA_MANAGER', 'ASM'],
      items: [
        {
          label: 'Terminal Monitor',
          route: '/terminals',
          permissionKey: 'terminals.monitor',
        },
      ],
    },
    {
      id: 'controlpanel',
      label: 'Control Panel',
      icon: 'bi-gear',
      roles: ['Admin'],
      items: [
        {
          label: 'Users',
          route: '/users',
          permissionKey: 'controlpanel.users',
          subRoutes: ['/users/create', '/users/edit'],
        },
        {
          label: 'GST Slabs',
          route: '/gst-slab',
          permissionKey: 'controlpanel.gst-slabs',
          subRoutes: ['/gst-slab/create', '/gst-slab/edit'],
        },
        {
          label: 'Locations',
          route: '/locations',
          permissionKey: 'controlpanel.locations',
          subRoutes: ['/locations/create', '/locations/edit'],
        },
        {
          label: 'Reason Master',
          route: '/reason-master',
          permissionKey: 'controlpanel.reason-master',
        },
        {
          label: 'Roles & Access',
          route: '/roles-access',
          permissionKey: 'controlpanel.roles-access',
          roles: ['Admin', 'SUPER_ADMIN'],
        },
        {
          label: 'Configuration',
          route: '/configuration',
          permissionKey: 'controlpanel.configuration',
          roles: ['Admin', 'SUPER_ADMIN'],
        },
        {
          label: 'Wallet',
          route: '/wallet',
          roles: ['Admin', 'SUPER_ADMIN'],
        },
      ],
    },
    {
      // Platform operator: tenants, templates, pricing. Shown to SUPER_ADMIN
      // whether or not a workspace is selected.
      id: 'superadmin',
      label: 'Super Admin',
      icon: 'bi-shield-lock',
      superAdminOnly: true,
      items: [
        {
          label: 'Workspaces',
          route: '/super-admin/clients',
          subRoutes: ['/super-admin/onboard'],
        },
        {
          label: 'Wallet & Pricing',
          route: '/super-admin/wallet-pricing',
        },
        {
          label: 'Usage & Billing',
          route: '/super-admin/usage',
        },
        {
          label: 'Industry Templates',
          route: '/super-admin/industry-templates',
        },
      ],
    },
  ];

  /**
   * Item visibility: RAC page permissions when loaded, otherwise the legacy
   * hardcoded role lists. Items without a permissionKey always use roles.
   */
  private isItemVisible(item: NavItem): boolean {
    if (item.permissionKey && this.permissionService.hasPermissionData()) {
      return this.permissionService.canView(item.permissionKey);
    }
    if (item.roles && !this.authService.hasRole(item.roles)) return false;
    return true;
  }

  public getVisibleMenu(): NavCategory[] {
    const racActive = this.permissionService.hasPermissionData();
    // Read the flags version so signal-based consumers (sidebar) re-run
    // when the Configuration switches arrive or change.
    this.moduleFlags.version();
    const isSuperAdmin = this.authService.isSuperAdmin();
    // A super admin who has not entered a workspace has no tenant data to
    // show: only the platform-level screens make sense.
    const platformOnly = isSuperAdmin && !this.authService.getSelectedTenantId();
    return this.menuStructure
      .filter((cat: NavCategory) => {
        if (cat.superAdminOnly) return isSuperAdmin;
        if (platformOnly) return false;
        if (cat.settingKey && !this.moduleFlags.isEnabled(cat.settingKey))
          return false;
        // With RAC data the category shows iff any of its items is granted;
        // the legacy category role gate only applies as fallback.
        if (!racActive && cat.roles && !this.authService.hasRole(cat.roles))
          return false;
        if (
          cat.clientType &&
          this.authService.getClientType() !== cat.clientType
        )
          return false;
        return true;
      })
      .map((cat: NavCategory) => ({
        ...cat,
        items: cat.items.filter((item: NavItem) => this.isItemVisible(item)),
      }))
      .filter((cat: NavCategory) => cat.items.length > 0);
  }

  public getActiveCategory(): NavCategory | undefined {
    const currentUrl = this.router.url.split('?')[0];
    const fullUrl = this.router.url;

    return this.getVisibleMenu().find((cat: NavCategory) => {
      return cat.items.some((item: NavItem) => {
        // Special case for E-Way Bill
        if (item.queryParams?.tab === 'eway_bill') {
          if (fullUrl.includes('tab=eway_bill')) return true;
        } else if (
          fullUrl.includes('tab=eway_bill') &&
          item.route === '/transfer-out'
        ) {
          return false;
        }

        if (item.route === currentUrl) return true;
        if (item.subRoutes?.some((sr) => currentUrl.startsWith(sr)))
          return true;
        return false;
      });
    });
  }

  public getTabsForActiveCategory(): NavItem[] {
    const activeCat = this.getActiveCategory();
    if (!activeCat) return [];

    // Filter again just in case (getVisibleMenu already filtered)
    return activeCat.items.filter((item: NavItem) => this.isItemVisible(item));
  }

  /** True when `item` owns the current URL (same rules as the sidebar/tabs). */
  public isItemActive(item: NavItem): boolean {
    const currentUrl = this.router.url.split('?')[0];
    const fullUrl = this.router.url;

    if (item.queryParams?.tab) {
      return fullUrl.includes(`tab=${item.queryParams.tab}`);
    }
    if (item.route === currentUrl && !fullUrl.includes('tab=')) return true;
    return item.subRoutes?.some((sr) => currentUrl.startsWith(sr)) || false;
  }

  /**
   * Visible categories bundled into rail groups (see nav-groups.ts). A
   * category that is not listed in any group is appended as its own group so
   * nothing a user may access ever disappears from the menu.
   */
  public getVisibleGroups(): NavGroup[] {
    const visible = this.getVisibleMenu();
    const byId = new Map(visible.map((c) => [c.id, c]));
    const used = new Set<string>();
    const groups: NavGroup[] = [];

    for (const def of NAV_GROUPS) {
      const categories = def.categories
        .map((id) => byId.get(id))
        .filter((c): c is NavCategory => !!c);
      categories.forEach((c) => used.add(c.id));
      if (categories.length) {
        groups.push({ id: def.id, label: def.label, icon: def.icon, categories });
      }
    }
    for (const cat of visible) {
      if (!used.has(cat.id)) {
        groups.push({ id: cat.id, label: cat.label, icon: 'products', categories: [cat] });
      }
    }
    return groups;
  }

  public getActiveGroup(): NavGroup | undefined {
    const activeCat = this.getActiveCategory();
    if (!activeCat) return undefined;
    return this.getVisibleGroups().find((g) =>
      g.categories.some((c) => c.id === activeCat.id),
    );
  }

  /** Group / Module / Page trail for the top bar. */
  public getBreadcrumb(): NavCrumb[] {
    const group = this.getActiveGroup();
    const cat = this.getActiveCategory();
    if (!group || !cat) return [];
    const item = cat.items.find((i) => this.isItemActive(i));
    const crumbs: NavCrumb[] = [{ label: group.label }];
    if (group.categories.length > 1 || !item) {
      crumbs.push({
        label: cat.label,
        route: item ? cat.items[0]?.route : undefined,
        queryParams: item ? cat.items[0]?.queryParams : undefined,
      });
    }
    if (item) crumbs.push({ label: item.label, route: item.route, queryParams: item.queryParams });
    return crumbs;
  }
}
